import {
  collection,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  setDoc,
  deleteDoc,
  runTransaction,
  writeBatch,
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
  getDocs,
} from 'firebase/firestore';
import { firestore } from './config';
import { subirACloudinary } from '@/services/cloudinary';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { PetshopId, Petshop, PetshopVenta, PetshopRendicion, PetshopProducto, MetodoPago } from '@/types/domain';

const ventaConv: FirestoreDataConverter<PetshopVenta> = {
  toFirestore(d: WithFieldValue<PetshopVenta>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<PetshopVenta, 'id'>;
    return { id: snap.id, ...data };
  },
};

const rendConv: FirestoreDataConverter<PetshopRendicion> = {
  toFirestore(d: WithFieldValue<PetshopRendicion>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<PetshopRendicion, 'id'>;
    return { id: snap.id, ...data };
  },
};

const productoConv: FirestoreDataConverter<PetshopProducto> = {
  toFirestore(d: WithFieldValue<PetshopProducto>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<PetshopProducto, 'id'>;
    return { id: snap.id, ...data };
  },
};

export const petshopVentasCollection = collection(firestore, 'petshop_ventas').withConverter(ventaConv);
export const petshopRendicionesCollection = collection(firestore, 'petshop_rendiciones').withConverter(rendConv);
export const petshopProductosCollection = collection(firestore, 'petshop_productos').withConverter(productoConv);

// ---- Petshops (autoservicio) ----
export const petshopsCollection = collection(firestore, 'petshops');

export const petshopsQuery: any = USE_API
  ? apiQuery('/petshops', { _order: 'orden' })
  : query(petshopsCollection, orderBy('orden'));

export interface UpsertPetshopInput {
  id?: string;
  nombre: string;
  nombreCorto?: string | null;
  dueno?: string | null;
  telefono?: string | null;
  qrUrl?: string | null;
}

export async function upsertPetshop(input: UpsertPetshopInput): Promise<string> {
  const body = {
    nombre: input.nombre,
    nombreCorto: input.nombreCorto ?? null,
    dueno: input.dueno ?? null,
    telefono: input.telefono ?? null,
    qrUrl: input.qrUrl ?? null,
  };
  if (USE_API) {
    if (input.id) return (await api.patch(`/petshops/${input.id}`, body)).id ?? input.id;
    return (await api.post('/petshops', { ...body, orden: 100 })).id;
  }
  const id = input.id ?? doc(petshopsCollection).id;
  await setDoc(doc(petshopsCollection, id), { ...body, ...(input.id ? {} : { orden: 100, activo: true }) } as any, { merge: true });
  return id;
}

export function productosPetshop(petshop: PetshopId): any {
  if (USE_API) return apiQuery('/petshop_productos', { petshop, _order: 'nombre' });
  return query(petshopProductosCollection, where('petshop', '==', petshop), orderBy('nombre'));
}

/** Todas las ventas del petshop, incluidas rendidas. */
export function ventasPetshop(petshop: PetshopId): any {
  if (USE_API) return apiQuery('/petshop_ventas', { petshop, _order: '-fecha' });
  return query(petshopVentasCollection, where('petshop', '==', petshop), orderBy('fecha', 'desc'));
}

export interface UpsertProductoPetshopInput {
  id?: string;
  petshop: PetshopId;
  nombre: string;
  categoria: string;
  precio: number;
  stock?: number;
  notas?: string;
}

export async function upsertProductoPetshop(input: UpsertProductoPetshopInput) {
  if (USE_API) {
    if (input.id) return (await api.patch(`/petshop_productos/${input.id}`, input)).id;
    return (await api.post('/petshop_productos', input)).id;
  }
  const id = input.id ?? doc(petshopProductosCollection).id;
  const ref_ = doc(petshopProductosCollection, id);
  await setDoc(
    ref_,
    {
      petshop: input.petshop,
      nombre: input.nombre,
      categoria: input.categoria,
      precio: input.precio,
      stock: input.stock ?? 0,
      notas: input.notas ?? null,
      updatedAt: serverTimestamp(),
      ...(input.id ? {} : { createdAt: serverTimestamp() }),
    } as any,
    { merge: true },
  );
  return id;
}

export async function eliminarProductoPetshop(id: string) {
  if (USE_API) return void (await api.delete(`/petshop_productos/${id}`));
  await deleteDoc(doc(petshopProductosCollection, id));
}

/** Fija el stock de un producto (reponer / ajustar). */
export async function ajustarStockPetshop(id: string, nuevoStock: number) {
  if (USE_API) return void (await api.patch(`/petshop_productos/${id}`, { stock: nuevoStock }));
  await setDoc(doc(petshopProductosCollection, id), { stock: nuevoStock } as any, { merge: true });
}

/** Ventas pendientes (sin rendir) de un petshop. */
export function ventasPendientes(petshop: PetshopId): any {
  if (USE_API) return apiQuery('/petshop_ventas', { petshop, rendicion_null: 'true', _order: '-fecha' });
  return query(
    petshopVentasCollection,
    where('petshop', '==', petshop),
    where('rendicionId', '==', null),
    orderBy('fecha', 'desc'),
  );
}

/** Histórico de rendiciones de un petshop. */
export function rendicionesPara(petshop: PetshopId): any {
  if (USE_API) return apiQuery('/petshop_rendiciones', { petshop, _order: '-rendida_en' });
  return query(petshopRendicionesCollection, where('petshop', '==', petshop), orderBy('rendidaEn', 'desc'));
}

export interface CrearVentaInput {
  petshop: PetshopId;
  descripcion: string;
  monto: number;
  metodoPago: MetodoPago;
  productoId?: string | null;
  productoCategoria?: string | null;
  fecha: Date;
  autorUid: string;
  autorNombre: string;
}

export async function crearVentaPetshop(input: CrearVentaInput) {
  if (USE_API) {
    await api.post('/acciones/ventas-petshop', {
      petshop: input.petshop,
      metodoPago: input.metodoPago,
      items: [{ nombre: input.descripcion, precio: input.monto, cantidad: 1, productoId: input.productoId, categoria: input.productoCategoria }],
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    });
    return;
  }
  const id = doc(petshopVentasCollection).id;
  await setDoc(doc(petshopVentasCollection, id), {
    petshop: input.petshop,
    descripcion: input.descripcion,
    monto: input.monto,
    metodoPago: input.metodoPago,
    productoId: input.productoId ?? null,
    productoCategoria: input.productoCategoria ?? null,
    fecha: Timestamp.fromDate(input.fecha),
    autorUid: input.autorUid,
    autorNombre: input.autorNombre,
    rendicionId: null,
    createdAt: serverTimestamp(),
  } as any);
}

export async function eliminarVentaPetshop(id: string) {
  if (USE_API) return void (await api.delete(`/petshop_ventas/${id}`));
  await deleteDoc(doc(petshopVentasCollection, id));
}

/** Anula una venta: devuelve el stock, la deja registrada como anulada (con motivo). */
export async function anularVentaPetshop(input: { ventaId: string; motivo: string; autorUid: string; autorNombre: string }) {
  if (USE_API) {
    await api.post('/acciones/anular-venta-petshop', input);
    return;
  }
  // Firebase: marca anulada (sin devolución de stock transaccional completa).
  await setDoc(
    doc(petshopVentasCollection, input.ventaId),
    { anulada: true, anuladaEn: serverTimestamp(), anuladaPorNombre: input.autorNombre, motivoAnulacion: input.motivo } as any,
    { merge: true },
  );
}

export interface ItemCarritoVenta {
  productoId: string | null;
  nombre: string;
  categoria: string | null;
  precio: number;
  cantidad: number;
}

export interface CrearVentasInput {
  petshop: PetshopId;
  items: ItemCarritoVenta[];
  metodoPago: MetodoPago;
  fecha: Date;
  autorUid: string;
  autorNombre: string;
}

export async function crearVentasPetshop(input: CrearVentasInput) {
  if (!input.items.length) throw new Error('Agregá al menos un producto.');
  if (USE_API) {
    await api.post('/acciones/ventas-petshop', {
      petshop: input.petshop,
      metodoPago: input.metodoPago,
      items: input.items,
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    });
    return;
  }
  const batch = writeBatch(firestore);
  const fechaTs = Timestamp.fromDate(input.fecha);
  for (const item of input.items) {
    const id = doc(petshopVentasCollection).id;
    const descripcion = item.cantidad > 1 ? `${item.nombre} ×${item.cantidad}` : item.nombre;
    const monto = item.precio * item.cantidad;
    batch.set(doc(petshopVentasCollection, id), {
      petshop: input.petshop,
      descripcion,
      monto,
      metodoPago: input.metodoPago,
      productoId: item.productoId ?? null,
      productoCategoria: item.categoria ?? null,
      fecha: fechaTs,
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
      rendicionId: null,
      createdAt: serverTimestamp(),
    } as any);
  }
  await batch.commit();
}

export async function subirComprobante(petshop: PetshopId, rendicionId: string, file: Blob, filename: string) {
  const folder = `clinicat/rendiciones/${petshop}`;
  const result = await subirACloudinary(file, { folder, filename: `${rendicionId}-${filename}` });
  return { url: result.url, path: result.publicId };
}

export interface RendirInput {
  petshop: PetshopId;
  metodoTransferencia: MetodoPago;
  comprobante?: { file: Blob; filename: string };
  notas?: string;
  autorUid: string;
  autorNombre: string;
}

export async function rendirPetshop(input: RendirInput) {
  if (USE_API) {
    let comprobanteUrl: string | null = null;
    let comprobanteNombre: string | null = null;
    if (input.comprobante) {
      const up = await subirComprobante(input.petshop, `rend-${Date.now()}`, input.comprobante.file, input.comprobante.filename);
      comprobanteUrl = up.url;
      comprobanteNombre = input.comprobante.filename;
    }
    const res = await api.post('/acciones/rendir-petshop', {
      petshop: input.petshop,
      metodoTransferencia: input.metodoTransferencia,
      comprobanteUrl,
      comprobanteNombre,
      notas: input.notas ?? null,
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    });
    return { ...res, comprobanteUrl };
  }

  const snap = await getDocs(ventasPendientes(input.petshop) as Query<PetshopVenta>);
  if (snap.empty) throw new Error('No hay ventas para rendir.');
  const ventas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const total = ventas.reduce((sum, v) => sum + (v.monto ?? 0), 0);
  const ahora = new Date();
  const mesISO = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
  const rendicionId = doc(petshopRendicionesCollection).id;
  const rendicionRef = doc(petshopRendicionesCollection, rendicionId);
  let comprobanteUrl: string | null = null;
  let comprobanteNombre: string | null = null;
  if (input.comprobante) {
    const { url } = await subirComprobante(input.petshop, rendicionId, input.comprobante.file, input.comprobante.filename);
    comprobanteUrl = url;
    comprobanteNombre = input.comprobante.filename;
  }
  await runTransaction(firestore, async (tx) => {
    tx.set(rendicionRef, {
      petshop: input.petshop,
      mesISO,
      total,
      cantidadVentas: ventas.length,
      metodoTransferencia: input.metodoTransferencia,
      comprobanteUrl,
      comprobanteNombre,
      notas: input.notas ?? null,
      rendidaPorUid: input.autorUid,
      rendidaPorNombre: input.autorNombre,
      rendidaEn: serverTimestamp(),
    } as any);
    for (const v of ventas) {
      tx.update(doc(petshopVentasCollection, v.id), { rendicionId });
    }
  });
  return { rendicionId, total, cantidadVentas: ventas.length, comprobanteUrl };
}
